import { ChangeEvent, useState } from 'react';
import { CoreBox, CoreCheckbox, CoreChip, CoreTooltip, CoreTypography } from '@youscience/core';
import { TaskStatus, Task } from '@common-types';

import { FILTER_TAGS_COLORS } from '@constants/educationPlanner';

import { ConfirmationMessage } from '@components/ConfirmationMessage';

import useEducationPlannerStore from '@store/EducationPlannerStore';

import FlagIcon from '@images/flag.svg?react';
import ResourceIcon from '@images/resource.svg?react';
import PencilIcon from '@images/pencil.svg?react';
import BucketIcon from '@images/bucket-button.svg?react';
import RestoreTaskIcon from '@images/restore-task.svg?react';

import {
  sxStyles,
  StyledTaskCardWrap,
  StyledTaskCardInner,
  StyledCheckBoxWrap,
  StyledDueDate,
  StyledResourceLink,
} from './TaskCard.styles';

const MAX_DESCRIPTION_LENGTH = 200;
const MAX_ELLIPSIS_DESCRIPTION_LENGTH = 180;

const MIN_RESOURCE_COUNT = 1;

interface TaskCardProps {
  task: Task;
  handleEditTask: (task: Task) => void;
}

export const TaskCard = (props: TaskCardProps) => {
  const { task, handleEditTask } = props;

  const [isShowLessDescription, setIsShowLessDescription] = useState(false);
  const [isOpenConfirmationMessage, setIsOpenConfirmationMessage] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<TaskStatus | null>(null);

  const {
    changeTaskStatus,
    restoreTask,
    deleteTask,
    setEducationPlannerData,
    educationPlannerData: { pageTasks, isTaskArchivePage },
  } = useEducationPlannerStore((state) => state);

  const handleLearnMoreDescription = () => {
    setIsShowLessDescription((prevState) => !prevState);
  };

  const getTaskDescription = () => {
    if (!task?.description) return '';

    if (task.description.length <= MAX_DESCRIPTION_LENGTH) return task.description;

    return (
      <>
        {isShowLessDescription ? task.description : `${task.description.slice(0, MAX_ELLIPSIS_DESCRIPTION_LENGTH)}...`}
        &nbsp;
        <CoreTypography sx={sxStyles.learnMoreButton} onClick={handleLearnMoreDescription} component="span">
          {isShowLessDescription ? 'show less' : 'show more'}
        </CoreTypography>
      </>
    );
  };

  const handleCheckBoxChange = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newStatus = checked ? TaskStatus.COMPLETED : TaskStatus.ACTIVE;

    setOptimisticStatus(newStatus);

    try {
      await changeTaskStatus(event.target.value, newStatus);

      setOptimisticStatus(null);

      if (checked) {
        const currentState = useEducationPlannerStore.getState();
        const currentPageTasks = currentState.educationPlannerData.pageTasks;
        const completedTasksCount = currentPageTasks.filter((t) => t.status === TaskStatus.COMPLETED).length;

        if (completedTasksCount === currentPageTasks.length) {
          setTimeout(() => {
            void setEducationPlannerData({ isShowConfetti: true });
          }, 1000);
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticStatus(null);
    }
  };

  const handleOpenConfirmationMessage = () => {
    setIsOpenConfirmationMessage(true);
  };

  const handleDeleteTask = async () => {
    if (!task._id) return;

    await deleteTask(task._id.toString(), !!task.taskId);
  };

  const handleRestoreTask = () => {
    void restoreTask(task._id!.toString());

    if (pageTasks.length === 1) {
      setEducationPlannerData({ currentSeason: task.season, isTaskArchivePage: false });
    }
  };

  const formatDate = (date: string) => {
    const dateParts = date.split('-');
    const dateObject = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
    const formattedDate = dateObject.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

    return formattedDate;
  };

  return (
    <>
      <StyledTaskCardWrap data-testid="taskCard">
        <StyledTaskCardInner>
          <CoreBox>
            <StyledCheckBoxWrap>
              <CoreCheckbox
                data-testid="checkBoxTaskStatus"
                value={task._id}
                checked={(optimisticStatus ?? task?.status) === TaskStatus.COMPLETED}
                onChange={handleCheckBoxChange}
                disabled={isTaskArchivePage}
              />

              <CoreTypography data-testid="taskTitle" variant="h6" component="h5">
                {task?.title}
              </CoreTypography>
            </StyledCheckBoxWrap>

            <CoreTypography data-testid="taskDescription">{getTaskDescription()}</CoreTypography>

            {task.linkUrls && task.linkUrls?.length > 0 && task.linkUrls?.length > MIN_RESOURCE_COUNT && (
              <CoreBox sx={sxStyles.multipleResources}>
                {task?.linkUrls?.map((link, index) => (
                  <CoreTooltip key={link} title={link}>
                    <StyledResourceLink data-testid="resourceLink" href={link} target="_blank" rel="noreferrer">
                      <ResourceIcon />

                      <CoreTypography variant="caption">{`Resource ${index + 1}`}</CoreTypography>
                    </StyledResourceLink>
                  </CoreTooltip>
                ))}
              </CoreBox>
            )}
          </CoreBox>

          <CoreBox sx={sxStyles.cardFooter}>
            <CoreBox sx={sxStyles.footerInfo}>
              {task?.dueDate && (
                <StyledDueDate>
                  <FlagIcon />

                  <CoreTypography data-testid="dueDate" variant="caption">
                    Finish before {formatDate(task.dueDate)}
                  </CoreTypography>
                </StyledDueDate>
              )}

              {task?.linkUrls?.length === MIN_RESOURCE_COUNT && (
                <CoreTooltip title={task.linkUrls[0]}>
                  <StyledResourceLink
                    data-testid="resourceLink"
                    href={task.linkUrls[0]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ResourceIcon />

                    <CoreTypography variant="caption">Resource</CoreTypography>
                  </StyledResourceLink>
                </CoreTooltip>
              )}

              {task?.tags?.map((item) => (
                <CoreChip
                  data-testid="filterChip"
                  key={item}
                  sx={{ color: FILTER_TAGS_COLORS[item], borderRadius: '8px' }}
                  variant="outlined"
                  label={item}
                />
              ))}
            </CoreBox>

            <CoreBox sx={sxStyles.footerActions}>
              {!task.taskId && !task.isDeleted && (
                <CoreTooltip title="Edit">
                  <CoreBox data-testid="editTaskIcon" sx={sxStyles.actionIcon} onClick={() => handleEditTask(task)}>
                    <PencilIcon />
                  </CoreBox>
                </CoreTooltip>
              )}

              {task.isDeleted ? (
                <CoreTooltip title="Restore this task to your timeline">
                  <CoreBox data-testid="restoreTaskIcon" sx={sxStyles.actionIcon} onClick={handleRestoreTask}>
                    <RestoreTaskIcon />
                  </CoreBox>
                </CoreTooltip>
              ) : (
                <CoreTooltip title="Remove this task from your timeline">
                  <CoreBox
                    data-testid="removeTaskIcon"
                    sx={sxStyles.actionIcon}
                    onClick={handleOpenConfirmationMessage}
                  >
                    <BucketIcon />
                  </CoreBox>
                </CoreTooltip>
              )}
            </CoreBox>
          </CoreBox>
        </StyledTaskCardInner>
      </StyledTaskCardWrap>

      {isOpenConfirmationMessage && (
        <ConfirmationMessage
          icon={<BucketIcon />}
          title="You are about to remove this task from your plan."
          subtitle="If you are unsure if you need to remove it, please ask your school counselor."
          isOpenConfirmationMessage={isOpenConfirmationMessage}
          setIsOpenConfirmationMessage={setIsOpenConfirmationMessage}
          onGoNextBtnClick={handleDeleteTask}
        />
      )}
    </>
  );
};
